import clsx from 'clsx';
import InfoIconWIthTooltip from 'components/molecules/InfoIconWithTooltip/InfoIconWIthTooltip';
import { useField, useFormikContext } from 'formik';
import {
  AssessmentExpiryOption,
  ProjectTeamEmailMode,
} from 'generated/graphql';
import { TCognitiveSkillsVersion } from 'helpers/cognitiveSkills';
import { projectTeamEmailModeOptions } from 'helpers/projectTeamEmailMode';
import { FC, useEffect, useMemo } from 'react';
import {
  FormikCheckboxFormField,
  FormikDatepicker,
  FormikMultiselectFormField,
  FormikSelectFormField,
  FormikTextInputField,
  TMultiselectOption,
} from '@spotted-zebra-uk/ui-components';
import { STAGE_NAME_MAX_LENGTH } from '../helpers';
import CognitiveModeField from './CognitiveModeField';
import ReportsSection from './ReportsSection';
import {
  ASSESSMENT_EXPIRY_TOOLTIP_CONTENT,
  assessmentExpiryDurationOptions,
} from './StageForm.helper';
import { IStageFormValues } from './StageForm.interfaces';
import styles from './StageForm.module.scss';

const StageFormFields = ({
  companyExpiryOption,
  cognitiveSkillsVersion,
  hasRoleOnlyOneCognitiveSkill,
  projectRoles,
  companyHideReports,
}: {
  projectRoles: TMultiselectOption[];
  companyExpiryOption: AssessmentExpiryOption | null;
  cognitiveSkillsVersion: TCognitiveSkillsVersion;
  hasRoleOnlyOneCognitiveSkill: boolean;
  companyHideReports: boolean;
}) => {
  const { values, setFieldValue, setFieldError, errors } =
    useFormikContext<IStageFormValues>();

  useEffect(() => {
    // Its function of this useEffect is to monitor changes in the 'stageMode' field and clear any existing errors on 'stageProjectRoles' if the stage mode changes to a type that does not require project roles (e.g., any mode other than "Role Based" or "Role Based and Assignee Based").

    // This ensures that the stageProjectRoles dropdown does not display validation errors that are no longer relevant. For example:
    // - If the user initially selects a mode that requires project roles and does not select any roles, an error will be shown on submit ( "This is a required field.").
    // - If the user then changes the stage mode to one that does not require project roles, this component will clear the error automatically
    if (
      values.stageMode.value !== ProjectTeamEmailMode.ProjectRoleBased &&
      values.stageMode.value !==
        ProjectTeamEmailMode.ProjectRoleAndAssigneeBased
    ) {
      setFieldError('stageProjectRoles', undefined);
    }
  }, [errors.stageProjectRoles, values.stageMode.value, setFieldError]);

  const assessmentExpiryOptions = useMemo(() => {
    return assessmentExpiryDurationOptions.map(duration => {
      let labelDescription = undefined;
      if (companyExpiryOption?.duration?.toString() === duration) {
        labelDescription = `Company default`;
      }

      return {
        value: duration,
        labelDescription,
        label: duration !== 'none' ? `${duration} days` : 'Never expires',
      };
    });
  }, [companyExpiryOption]);

  return (
    <div className={styles.form}>
      <div className={styles.section}>
        <p className={styles.sectionTitle}>General</p>

        <div className={styles.row}>
          <FormikTextInputField
            label="Name"
            id="stageName"
            placeholder="Stage name"
            className={styles.nameField}
            data-role="stage-name"
            useFormikField={useField}
            maxLength={STAGE_NAME_MAX_LENGTH}
            maxCharacters={STAGE_NAME_MAX_LENGTH}
            maxCharactersError={
              values.stageName.length >= STAGE_NAME_MAX_LENGTH
            }
          />
        </div>
        <div className={styles.row}>
          <div data-testid="stage-form__start-time-select">
            <FormikDatepicker
              id="stageStartTime"
              name="stageStartTime"
              label="Start at"
              className={styles.startTimeDatepicker}
              useFormikField={useField}
            />
          </div>
          <EndAtFormikDatepicker />
          <div className={styles.endTimeCheckbox}>
            <FormikCheckboxFormField
              id="stageIsEndTimeNull"
              name="stageIsEndTimeNull"
              label="No end time"
              useFormikField={useField}
            />
          </div>
        </div>
      </div>

      <div
        className={styles.cognitiveAssessmentModeSection}
        data-testid="stage-form-fields__cognitive-assessment-mode-section"
      >
        <p className={styles.sectionTitle}>Cognitive assessment mode</p>
        {cognitiveSkillsVersion ? (
          <CognitiveModeField
            hasRoleOnlyOneCognitiveSkill={hasRoleOnlyOneCognitiveSkill}
            cognitiveSkillsVersion={cognitiveSkillsVersion}
          />
        ) : (
          <span className={styles.cognitiveAssessmentModeGuidance}>
            Cognitive assessment mode selection is unavailable because the role
            or roles added do not include any cognitive abilities.
          </span>
        )}
      </div>

      {companyExpiryOption && (
        <div
          className={styles.expirySection}
          data-testid="stage-form-fields__expiry-section"
        >
          <div className={styles.sectionTitle}>
            Assessment expiry{' '}
            <InfoIconWIthTooltip content={ASSESSMENT_EXPIRY_TOOLTIP_CONTENT} />
          </div>
          <FormikSelectFormField
            id="stageAssessmentExpiry"
            label="Validity period"
            options={assessmentExpiryOptions}
            useFormikField={useField}
            hasClearIndicator={false}
          />
        </div>
      )}
      <div className={styles.section}>
        <p className={styles.sectionTitle}>Reports</p>
        <ReportsSection companyHideReports={companyHideReports} />
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Assessment completion emails</h3>
        {/* 
        The stageMode options "Access Based" and "Assignee Based" will leave the Project Roles dropdown as disabled. However, if "Project role based" or "Project role based & assignee based" are selected, the Project Role dropdown becomes enabled. If the Project role dropdown has an error and the user goes back to change the stage mode (to "Access Based" for example), then the Project Role error should be removed. Due to issues with Formik on the Admin app, this behavior cannot be set in the stage mode dropdown onChange
      */}
        <div className={clsx(styles.row, styles.small)}>
          <FormikSelectFormField
            id="stageMode"
            label="Mode"
            options={projectTeamEmailModeOptions}
            onChange={() => {
              setFieldValue('stageProjectRoles', []);
            }}
            useFormikField={useField}
            hasClearIndicator={false}
          />
        </div>
        <div className={styles.row}>
          <FormikMultiselectFormField
            id="stageProjectRoles"
            label="Hiring role(s)"
            placeholder="Hiring role(s)"
            isDisabled={
              values.stageMode.value ===
                ProjectTeamEmailMode.ProjectRoleBased ||
              values.stageMode.value ===
                ProjectTeamEmailMode.ProjectRoleAndAssigneeBased
                ? false
                : true
            }
            options={projectRoles}
            useFormikField={useField}
          />
        </div>
      </div>
    </div>
  );
};

const EndAtFormikDatepicker: FC = () => {
  const { values, setFieldValue } = useFormikContext<IStageFormValues>();

  useEffect(() => {
    if (values.stageIsEndTimeNull) {
      setFieldValue('stageEndTime', '');
    }
  }, [values.stageIsEndTimeNull, setFieldValue]);

  return (
    <FormikDatepicker
      id="stageEndTime"
      name="stageEndTime"
      label="End at"
      className={styles.endTimeDatepicker}
      isDisabled={values.stageIsEndTimeNull}
      useFormikField={useField}
    />
  );
};

export default StageFormFields;
